/**
 * Audio to WAV conversion utilities
 * Handles conversion of various audio formats to WAV for backend compatibility
 */

/**
 * Convert any audio blob to WAV using Web Audio API
 * Much simpler than manual binary manipulation
 */
export async function convertAudioToWav(audioBlob: Blob): Promise<Blob> {
  if (audioBlob.type === 'audio/wav') {
    return audioBlob;
  }

  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const audioContext = new AudioContextClass();

    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Use OfflineAudioContext to render to WAV
    const offlineContext = new OfflineAudioContext(audioBuffer.numberOfChannels, audioBuffer.length, audioBuffer.sampleRate);

    const source = offlineContext.createBufferSource();

    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start(0);

    const renderedBuffer = await offlineContext.startRendering();
    const wavBlob = audioBufferToWav(renderedBuffer);

    audioContext.close();

    return wavBlob;
  } catch {
    // WAV conversion failed - error will be handled by caller

    return audioBlob; // Fallback to original
  }
}

/**
 * Convert AudioBuffer to WAV blob
 * Simplified version with just the essentials
 */
// eslint-disable-next-line max-statements
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const length = buffer.length;
  const channels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const arrayBuffer = new ArrayBuffer(44 + length * channels * 2);
  const view = new DataView(arrayBuffer);

  // WAV header (44 bytes)
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * channels * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM format
  view.setUint16(20, 1, true); // Audio format
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true); // 16-bit
  writeString(36, 'data');
  view.setUint32(40, length * channels * 2, true);

  // Convert audio data to 16-bit PCM
  let offset = 44;

  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < channels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));

      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}
